import { Component, viewChild, AfterViewInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  ButtonComponent,
  InputComponent,
  StepComponent,
  StepperComponent,
} from '@group24ag/flowkit';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'app-ui-stepper',
  imports: [
    StepperComponent,
    StepComponent,
    InputComponent,
    ButtonComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
  ],
  template: `
    <!-- ── Step content templates ──────────────────────────────── -->

    <!-- Default: step 1 -->
    <ng-template #defaultStep1 let-s="s">
      <div class="flex max-w-sm flex-col gap-3" [formGroup]="defaultFormGroup1">
        <div class="grid grid-cols-2 gap-3">
          <flk-input formControlName="firstName" [label]="'First name'" />
          <flk-input formControlName="lastName" [label]="'Last name'" />
        </div>
        <flk-input formControlName="email" [label]="'Email'" />
        <button
          class="mb-2"
          flkButton
          [appearance]="'primary'"
          [variant]="'accent'"
          (click)="s.next()">
          Next
        </button>
      </div>
    </ng-template>

    <!-- Default: step 2 -->
    <ng-template #defaultStep2 let-s="s">
      <div class="flex max-w-sm flex-col gap-3" [formGroup]="defaultFormGroup2">
        <flk-input formControlName="companyName" [label]="'Company name'" />
        <flk-input formControlName="jobTitle" [label]="'Job title'" />
        <div class="flex gap-2">
          <button
            flkButton
            [appearance]="'primary'"
            [variant]="'info'"
            (click)="s.previous()">
            Back
          </button>
          <button
            class="mb-2"
            flkButton
            [appearance]="'primary'"
            [variant]="'accent'"
            (click)="s.next()">
            Next
          </button>
        </div>
      </div>
    </ng-template>

    <!-- Default: step 3 (optional) -->
    <ng-template #defaultStep3 let-s="s">
      <div class="flex max-w-sm flex-col gap-3">
        <flk-input
          [label]="'New password'"
          [placeholder]="'••••••••'"
          [type]="'password'" />
        <flk-input
          [label]="'Confirm password'"
          [placeholder]="'••••••••'"
          [type]="'password'" />
        <div class="mb-2 flex gap-2">
          <button
            flkButton
            [appearance]="'primary'"
            [variant]="'info'"
            (click)="s.previous()">
            Back
          </button>
          <button
            flkButton
            [appearance]="'primary'"
            [variant]="'accent'"
            (click)="s.next()">
            Skip
          </button>
        </div>
      </div>
    </ng-template>

    <!-- Default: step 4 -->
    <ng-template #defaultStep4 let-s="s">
      <div class="flex max-w-sm flex-col gap-3">
        <flk-input [label]="'Display name'" [placeholder]="'Jane Doe'" />
        <div class="mb-2 flex gap-2">
          <button
            flkButton
            [appearance]="'primary'"
            [variant]="'info'"
            (click)="s.previous()">
            Back
          </button>
        </div>
      </div>
    </ng-template>

    <!-- Linear: step 1 -->
    <ng-template #linearStep1 let-s="s">
      <div
        class="flex max-w-sm flex-col gap-3"
        [formGroup]="lineStep1FormGroup">
        <flk-input formControlName="username" [label]="'Username'" />
        <flk-input formControlName="email" [label]="'Email'" />
        <button
          flkButton
          class="mb-2"
          [appearance]="'primary'"
          [variant]="'accent'"
          (click)="getStep(s, 0).completed = true; s.next()">
          Continue
        </button>
      </div>
    </ng-template>

    <!-- Linear: step 2 -->
    <ng-template #linearStep2 let-s="s">
      <div class="flex max-w-sm flex-col gap-3">
        <flk-input
          [label]="'Password'"
          [placeholder]="'••••••••'"
          [type]="'password'" />
        <flk-input
          [label]="'Confirm password'"
          [placeholder]="'••••••••'"
          [type]="'password'" />
        <button
          flkButton
          class="mb-2"
          [appearance]="'primary'"
          [variant]="'accent'"
          (click)="getStep(s, 1).completed = true; s.next()">
          Continue
        </button>
      </div>
    </ng-template>

    <!-- Linear: step 3 -->
    <ng-template #linearStep3 let-s="s">
      <div class="flex max-w-sm flex-col gap-3">
        <flk-input [label]="'Display name'" [placeholder]="'Jane Doe'" />
        <flk-input [label]="'Phone'" [placeholder]="'+49 30 000000'" />
        <button
          flkButton
          class="mb-2"
          [appearance]="'primary'"
          [variant]="'accent'"
          (click)="getStep(s, 2).completed = true; s.next()">
          Continue
        </button>
      </div>
    </ng-template>

    <!-- Linear: step 4 -->
    <ng-template #linearStep4 let-s="s">
      <div class="flex max-w-sm flex-col gap-3">
        <flk-input
          [label]="'Invite a colleague'"
          [placeholder]="'colleague&#64;example.com'" />
        <button
          flkButton
          class="mb-2"
          [appearance]="'primary'"
          [variant]="'accent'"
          (click)="getStep(s, 3).completed = true">
          Finish
        </button>
      </div>
    </ng-template>

    <!-- Disabled: step 1 -->
    <ng-template #disabledStep1 let-s="s">
      <div class="flex max-w-sm flex-col gap-3">
        <flk-input [label]="'First name'" [placeholder]="'Jane'" />
        <flk-input [label]="'Last name'" [placeholder]="'Doe'" />
        <button
          flkButton
          class="mb-2"
          [appearance]="'primary'"
          [variant]="'accent'"
          (click)="s.next()">
          Next
        </button>
      </div>
    </ng-template>

    <!-- Disabled: step 2 (placeholder — this step is disabled) -->
    <ng-template #disabledStep2>
      <p class="text-sm">This step is disabled.</p>
    </ng-template>

    <!-- Disabled: step 3 -->
    <ng-template #disabledStep3 let-s="s">
      <div class="flex max-w-sm flex-col gap-3">
        <flk-input [label]="'Company'" [placeholder]="'Acme Inc.'" />
        <flk-input [label]="'Department'" [placeholder]="'Engineering'" />
        <div class="mb-2 flex gap-2">
          <button
            flkButton
            [appearance]="'primary'"
            [variant]="'info'"
            (click)="s.previous()">
            Back
          </button>
          <button
            flkButton
            [appearance]="'primary'"
            [variant]="'accent'"
            (click)="s.next()">
            Next
          </button>
        </div>
      </div>
    </ng-template>

    <!-- Disabled: step 4 -->
    <ng-template #disabledStep4 let-s="s">
      <div class="flex max-w-sm flex-col gap-3">
        <flk-input [label]="'Notes'" [placeholder]="'Anything to add?'" />
        <div class="mb-2 flex gap-2">
          <button
            flkButton
            [appearance]="'primary'"
            [variant]="'info'"
            (click)="s.previous()">
            Back
          </button>
        </div>
      </div>
    </ng-template>

    <!-- ── Layout ───────────────────────────────────────────────── -->

    <div class="flex flex-col gap-12">
      <h1 class="heading-title">Stepper</h1>

      <!-- ── Horizontal ──────────────────────────────────────────── -->
      <section class="flex flex-col gap-10">
        <h2 class="text-sm font-semibold tracking-wider uppercase">
          Horizontal
        </h2>

        <div class="space-y-3">
          <h3 class="text-sm font-medium">Default</h3>
          <flk-stepper #hDefault orientation="horizontal" [lockHeaders]="true">
            <flk-step label="Personal Info" [formGroup]="defaultFormGroup1">
              <ng-container
                [ngTemplateOutlet]="defaultStep1"
                [ngTemplateOutletContext]="{ s: hDefault }" />
            </flk-step>
            <flk-step label="Company" [formGroup]="defaultFormGroup2">
              <ng-container
                [ngTemplateOutlet]="defaultStep2"
                [ngTemplateOutletContext]="{ s: hDefault }" />
            </flk-step>
            <flk-step label="Security" optional="Optional">
              <ng-container
                [ngTemplateOutlet]="defaultStep3"
                [ngTemplateOutletContext]="{ s: hDefault }" />
            </flk-step>
            <flk-step label="Confirm">
              <ng-container
                [ngTemplateOutlet]="defaultStep4"
                [ngTemplateOutletContext]="{ s: hDefault }" />
            </flk-step>
          </flk-stepper>
        </div>

        <div class="space-y-3">
          <h3 class="text-sm font-medium">Linear</h3>
          <flk-stepper #hLinear orientation="horizontal" [linear]="true">
            <flk-step label="Account" [formGroup]="lineStep1FormGroup">
              <ng-container
                [ngTemplateOutlet]="linearStep1"
                [ngTemplateOutletContext]="{ s: hLinear }" />
            </flk-step>
            <flk-step label="Password">
              <ng-container
                [ngTemplateOutlet]="linearStep2"
                [ngTemplateOutletContext]="{ s: hLinear }" />
            </flk-step>
            <flk-step label="Profile">
              <ng-container
                [ngTemplateOutlet]="linearStep3"
                [ngTemplateOutletContext]="{ s: hLinear }" />
            </flk-step>
            <flk-step label="Done">
              <ng-container
                [ngTemplateOutlet]="linearStep4"
                [ngTemplateOutletContext]="{ s: hLinear }" />
            </flk-step>
          </flk-stepper>
        </div>

        <div class="space-y-3">
          <h3 class="text-sm font-medium">With disabled step</h3>
          <flk-stepper #hDisabled orientation="horizontal">
            <flk-step label="General">
              <ng-container
                [ngTemplateOutlet]="disabledStep1"
                [ngTemplateOutletContext]="{ s: hDisabled }" />
            </flk-step>
            <flk-step label="Restricted" [disabled]="true">
              <ng-container [ngTemplateOutlet]="disabledStep2" />
            </flk-step>
            <flk-step label="Details">
              <ng-container
                [ngTemplateOutlet]="disabledStep3"
                [ngTemplateOutletContext]="{ s: hDisabled }" />
            </flk-step>
            <flk-step label="Confirm">
              <ng-container
                [ngTemplateOutlet]="disabledStep4"
                [ngTemplateOutletContext]="{ s: hDisabled }" />
            </flk-step>
          </flk-stepper>
        </div>
      </section>

      <!-- ── Vertical ────────────────────────────────────────────── -->
      <section class="flex flex-col gap-10">
        <h2 class="text-sm font-semibold tracking-wider uppercase">Vertical</h2>

        <div class="space-y-3">
          <h3 class="text-sm font-medium">Default</h3>
          <flk-stepper #vDefault orientation="vertical">
            <flk-step label="Personal Info" [formGroup]="defaultFormGroup1">
              <ng-container
                [ngTemplateOutlet]="defaultStep1"
                [ngTemplateOutletContext]="{ s: vDefault }" />
            </flk-step>
            <flk-step label="Company" [formGroup]="defaultFormGroup2">
              <ng-container
                [ngTemplateOutlet]="defaultStep2"
                [ngTemplateOutletContext]="{ s: vDefault }" />
            </flk-step>
            <flk-step label="Security" optional="Optional">
              <ng-container
                [ngTemplateOutlet]="defaultStep3"
                [ngTemplateOutletContext]="{ s: vDefault }" />
            </flk-step>
            <flk-step label="Confirm">
              <ng-container
                [ngTemplateOutlet]="defaultStep4"
                [ngTemplateOutletContext]="{ s: vDefault }" />
            </flk-step>
          </flk-stepper>
        </div>

        <div class="space-y-3">
          <h3 class="text-sm font-medium">Linear</h3>
          <flk-stepper #vLinear orientation="vertical" [linear]="true">
            <flk-step label="Account" [formGroup]="lineStep1FormGroup">
              <ng-container
                [ngTemplateOutlet]="linearStep1"
                [ngTemplateOutletContext]="{ s: vLinear }" />
            </flk-step>
            <flk-step label="Password">
              <ng-container
                [ngTemplateOutlet]="linearStep2"
                [ngTemplateOutletContext]="{ s: vLinear }" />
            </flk-step>
            <flk-step label="Profile">
              <ng-container
                [ngTemplateOutlet]="linearStep3"
                [ngTemplateOutletContext]="{ s: vLinear }" />
            </flk-step>
            <flk-step label="Done">
              <ng-container
                [ngTemplateOutlet]="linearStep4"
                [ngTemplateOutletContext]="{ s: vLinear }" />
            </flk-step>
          </flk-stepper>
        </div>

        <div class="space-y-3">
          <h3 class="text-sm font-medium">With disabled step</h3>
          <flk-stepper #vDisabled orientation="vertical">
            <flk-step label="General">
              <ng-container
                [ngTemplateOutlet]="disabledStep1"
                [ngTemplateOutletContext]="{ s: vDisabled }" />
            </flk-step>
            <flk-step #vDisabledStep2 label="Restricted" [disabled]="true">
              <ng-container [ngTemplateOutlet]="disabledStep2" />
            </flk-step>
            <flk-step label="Details">
              <ng-container
                [ngTemplateOutlet]="disabledStep3"
                [ngTemplateOutletContext]="{ s: vDisabled }" />
            </flk-step>
            <flk-step label="Confirm">
              <ng-container
                [ngTemplateOutlet]="disabledStep4"
                [ngTemplateOutletContext]="{ s: vDisabled }" />
            </flk-step>
          </flk-stepper>
        </div>
      </section>
    </div>
  `,
})
export class UIStepperComponent implements AfterViewInit {
  protected readonly lineStep1FormGroup = new FormGroup({
    username: new FormControl('', Validators.required),
    email: new FormControl('test@example.com', Validators.required),
  });

  /** Form groups for the Default stepper — drive [edited] and [isInvalid] on step 1 & 2 **/
  protected readonly defaultFormGroup1 = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    email: new FormControl('', Validators.required),
  });

  protected readonly defaultFormGroup2 = new FormGroup({
    companyName: new FormControl(''),
    jobTitle: new FormControl(''),
  });

  protected vDisabledStep2 =
    viewChild.required<StepComponent>('vDisabledStep2');

  ngAfterViewInit() {
    this.vDisabledStep2().completed = true;
  }

  protected getStep(stepper: any, index: number) {
    return stepper.getStep(index) as StepComponent;
  }
}
